import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Gavel, ShoppingBag, Clock, Users, ChevronLeft, ChevronRight, CheckCircle, Star, Quote, Share2 } from "lucide-react";
import { WhatsAppBadge } from "./WhatsAppBadge";
import { handleImgError } from "../lib/imageFallback";
import { shareProductWhatsApp } from "../lib/share";
import { useParticipations, participationLabel } from "../lib/participations";
import { useAuth } from "../App";

const ParticipationBadge = ({ productId, size = "sm" }) => {
  const { token } = useAuth();
  const { participations } = useParticipations(token);
  const type = participations[productId]?.type;
  if (!type) return null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-bold rounded-full bg-sky-500 text-white ${size === "lg" ? "text-[10px] px-2 py-0.5" : "text-[9px] px-1.5 py-0.5"}`}
      data-testid={`participation-badge-${productId}`}
    >
      <CheckCircle size={size === "lg" ? 10 : 9} /> {participationLabel(participations[productId])}
    </span>
  );
};

// Timer component for auctions
const AuctionTimer = ({ endTime, isCountdown = false }) => {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const end = new Date(endTime);
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft(isCountdown ? "¡Ya disponible!" : "Finalizado");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [endTime, isCountdown]);

  return <span>{timeLeft}</span>;
};

// Individual product card with overlay effect
const ProductOverlayCard = ({ product, isAuction: isAuctionProp = false, compact = false, fullWidth = false }) => {
  const isAuction = product.sale_type ? product.sale_type === "auction" : isAuctionProp;
  const hasPromo = !isAuction && !!product.promo_discount_percent && !!product.promo_price;
  const currentPrice = isAuction ? (product.current_bid || product.price) : (hasPromo ? product.promo_price : product.price);
  const bidCount = product.bid_count || 0;
  const requestCount = product.request_count || 0;
  
  // Check if product is scheduled (Próximamente)
  const isScheduled = product.status === 'scheduled' || 
    (product.scheduled_publish_time && new Date(product.scheduled_publish_time) > new Date());

  // Use taller card when displayed full-width (single product)
  const heightClass = fullWidth ? 'h-44' : (compact ? 'h-24' : 'h-28');

  return (
    <Link to={`/product/${product.product_id}`} className="block">
      <div className={`relative ${heightClass} rounded-xl overflow-hidden shadow-sm bg-[#F1F5F9]`}>
        {/* Background image - using <img> instead of CSS background to avoid Chrome mobile GPU rasterization bug */}
        <img
          src={product.image_url}
          alt={product.title}
          loading="lazy"
          decoding="async"
          onError={handleImgError}
          className={`absolute inset-0 w-full h-full object-cover ${isScheduled ? 'opacity-70' : ''}`}
        />
        
        {/* Gradient overlay */}
        <div className={`absolute inset-0 ${isScheduled ? 'bg-gradient-to-t from-amber-900/80 via-amber-800/40 to-amber-700/20' : 'bg-gradient-to-t from-black/70 via-black/25 to-transparent'}`} />
        
        {/* Top badge */}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          {isScheduled ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
              PRÓXIMAMENTE
            </span>
          ) : (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isAuction ? 'bg-blue-600 text-white' : 'bg-emerald-500 text-white'}`}>
              {isAuction ? 'SUBASTA' : 'VENTA'}
            </span>
          )}
          <ParticipationBadge productId={product.product_id} />
        </div>
        
        {/* Share via WhatsApp */}
        <button
          onClick={(e) => shareProductWhatsApp(product, e)}
          className={`absolute right-2 z-10 w-6 h-6 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center ${(isAuction && product.auction_end_time && !isScheduled) ? "top-8" : "top-2"}`}
          data-testid={`share-btn-${product.product_id}`}
          aria-label="Compartir por WhatsApp"
        >
          <Share2 size={11} />
        </button>
        
        {/* Timer */}
        {(isAuction && product.auction_end_time && !isScheduled) && (
          <div className="absolute top-2 right-2">
            <span className="text-[9px] bg-black/50 text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Clock size={9} />
              <AuctionTimer endTime={product.auction_end_time} />
            </span>
          </div>
        )}
        
        {/* Countdown for scheduled */}
        {isScheduled && product.scheduled_publish_time && (
          <div className="absolute top-2 right-2">
            <span className="text-[9px] bg-amber-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Clock size={9} />
              <AuctionTimer endTime={product.scheduled_publish_time} isCountdown={true} />
            </span>
          </div>
        )}
        
        {/* Content at bottom */}
        <div className={`absolute bottom-0 left-0 right-0 ${fullWidth ? 'p-3' : 'p-2'}`}>
          <h3 className={`font-bold text-white ${fullWidth ? 'text-sm' : (compact ? 'text-[11px]' : 'text-xs')} leading-tight line-clamp-1 drop-shadow-lg`}>
            {product.title}
          </h3>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1 flex-wrap">
              {hasPromo && (
                <span className="text-[9px] text-white/60 line-through">${product.price?.toLocaleString()}</span>
              )}
              <p className={`${fullWidth ? 'text-lg' : (compact ? 'text-sm' : 'text-base')} font-bold text-white drop-shadow-lg`}>
                ${currentPrice?.toLocaleString()}
              </p>
              {hasPromo && (
                <span className="text-[8px] font-bold px-1 py-0.5 rounded-full bg-red-500 text-white" data-testid={`discount-badge-${product.product_id}`}>
                  -{product.promo_discount_percent}%
                </span>
              )}
            </div>
            <span className={`${fullWidth ? 'text-[10px]' : 'text-[9px]'} text-white/80 flex items-center gap-0.5`}>
              {isAuction ? (
                <>
                  <Gavel size={fullWidth ? 11 : 9} />
                  {bidCount} ofertas
                </>
              ) : (
                <>
                  <Users size={fullWidth ? 11 : 9} />
                  {requestCount} solic.
                </>
              )}
            </span>
          </div>
        </div>
        
        {/* Scheduled overlay */}
        {isScheduled && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-amber-500/90 px-2 py-0.5 rounded-full">
              <span className="text-white text-[10px] font-semibold">Próximamente</span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

// Mobile 2x2 Grid layout with horizontal scroll
const ProductOverlayGrid = ({ products, isAuction = false, emptyMessage }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 5);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
      }
    };
    
    checkScroll();
    const ref = scrollRef.current;
    ref?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    
    return () => {
      ref?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [products]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const firstGrid = scrollRef.current.querySelector('.grid-item');
      const gridWidth = firstGrid?.offsetWidth || scrollRef.current.clientWidth - 32;
      scrollRef.current.scrollTo({
        left: direction === 'left' 
          ? Math.max(0, scrollRef.current.scrollLeft - gridWidth - 12)
          : scrollRef.current.scrollLeft + gridWidth + 12,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-[#94A3B8]">
        <p className="text-sm">{emptyMessage || "No hay productos"}</p>
      </div>
    );
  }

  // Group products in sets of 4 for each 2x2 grid
  const grids = [];
  for (let i = 0; i < products.length; i += 4) {
    grids.push(products.slice(i, i + 4));
  }

  return (
    <div className="relative">
      {/* Scroll buttons */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white/60 transition-colors"
        >
          <ChevronLeft size={18} className="text-[#374151]" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white/60 transition-colors"
        >
          <ChevronRight size={18} className="text-[#374151]" />
        </button>
      )}
      
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-2" 
        style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
      >
        {grids.map((gridProducts, gridIndex) => {
          const itemCount = gridProducts.length;
          // Determine grid layout based on item count for better UX (no empty cells)
          const gridColsClass = itemCount === 1 ? 'grid-cols-1' : 'grid-cols-2';
          return (
            <div 
              key={gridIndex} 
              className="grid-item flex-shrink-0"
              style={{ 
                width: 'calc(100vw - 32px)', 
                maxWidth: '420px',
                scrollSnapAlign: 'start'
              }}
            >
              <div className={`grid ${gridColsClass} gap-2`}>
                {gridProducts.map((product, idx) => {
                  // When 3 items in 2-col grid: make 3rd item span both cols (no empty cell)
                  const spanFull = itemCount === 3 && idx === 2;
                  return (
                    <div key={product.product_id} className={spanFull ? 'col-span-2' : ''}>
                      <ProductOverlayCard 
                        product={product} 
                        isAuction={isAuction}
                        fullWidth={itemCount === 1 || spanFull}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Desktop layout: Four sections in 2x2 grid
const DesktopHomeLayout = ({ auctionProducts, directProducts, finishedProducts, reviews, onDeleteReview, isAdmin, token, API }) => {
  const auctionScrollRef = useRef(null);
  const directScrollRef = useRef(null);
  const [auctionCanScroll, setAuctionCanScroll] = useState({ left: false, right: false });
  const [directCanScroll, setDirectCanScroll] = useState({ left: false, right: false });

  useEffect(() => {
    const checkScroll = (ref, setCanScroll) => {
      if (ref.current) {
        const { scrollLeft, scrollWidth, clientWidth } = ref.current;
        setCanScroll({
          left: scrollLeft > 5,
          right: scrollLeft < scrollWidth - clientWidth - 5
        });
      }
    };
    
    const checkAuction = () => checkScroll(auctionScrollRef, setAuctionCanScroll);
    const checkDirect = () => checkScroll(directScrollRef, setDirectCanScroll);
    
    checkAuction();
    checkDirect();
    
    const aRef = auctionScrollRef.current;
    const dRef = directScrollRef.current;
    aRef?.addEventListener('scroll', checkAuction);
    dRef?.addEventListener('scroll', checkDirect);
    
    return () => {
      aRef?.removeEventListener('scroll', checkAuction);
      dRef?.removeEventListener('scroll', checkDirect);
    };
  }, [auctionProducts, directProducts]);

  const scroll = (ref, direction) => {
    if (ref.current) {
      const firstGrid = ref.current.querySelector('.desktop-grid-item');
      const gridWidth = firstGrid?.offsetWidth || 300;
      ref.current.scrollTo({
        left: direction === 'left' 
          ? Math.max(0, ref.current.scrollLeft - gridWidth - 8)
          : ref.current.scrollLeft + gridWidth + 8,
        behavior: 'smooth'
      });
    }
  };

  const groupProducts = (products) => {
    const grids = [];
    for (let i = 0; i < products.length; i += 4) {
      grids.push(products.slice(i, i + 4));
    }
    return grids;
  };

  // Fallback: if there are no active auctions, show "Mejor Oferta" products so the quadrant isn't blank
  const auctionsFallbackActive = (auctionProducts || []).length === 0 && (directProducts || []).length > 0;
  const auctionDisplayProducts = auctionsFallbackActive ? directProducts : (auctionProducts || []);
  const auctionGrids = groupProducts(auctionDisplayProducts);
  const directGrids = groupProducts(directProducts || []);

  // Large card component for desktop grids
  const LargeProductCard = ({ product, isAuction: isAuctionProp }) => {
    const isAuction = product.sale_type ? product.sale_type === "auction" : isAuctionProp;
    const hasPromo = !isAuction && !!product.promo_discount_percent && !!product.promo_price;
    const currentPrice = isAuction ? (product.current_bid || product.price) : (hasPromo ? product.promo_price : product.price);
    const bidCount = product.bid_count || 0;
    const requestCount = product.request_count || 0;
    
    // Check if product is scheduled (Próximamente)
    const isScheduled = product.status === 'scheduled' || 
      (product.scheduled_publish_time && new Date(product.scheduled_publish_time) > new Date());

    return (
      <Link to={`/product/${product.product_id}`} className="block">
        <div className="relative h-40 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-[#F1F5F9]">
          <img
            src={product.image_url}
            alt={product.title}
            loading="lazy"
            decoding="async"
            onError={handleImgError}
            className={`absolute inset-0 w-full h-full object-cover ${isScheduled ? 'opacity-70' : ''}`}
          />
          <div className={`absolute inset-0 ${isScheduled ? 'bg-gradient-to-t from-amber-900/80 via-amber-800/40 to-amber-700/20' : 'bg-gradient-to-t from-black/70 via-black/20 to-transparent'}`} />
          
          <div className="absolute top-2 left-2 flex items-center gap-1">
            {isScheduled ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                PRÓXIMAMENTE
              </span>
            ) : (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isAuction ? 'bg-blue-600 text-white' : 'bg-emerald-500 text-white'}`}>
                {isAuction ? 'SUBASTA' : 'VENTA'}
              </span>
            )}
            <ParticipationBadge productId={product.product_id} size="lg" />
          </div>
          
          {/* Share via WhatsApp */}
          <button
            onClick={(e) => shareProductWhatsApp(product, e)}
            className={`absolute right-2 z-10 w-6 h-6 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 ${isAuction && product.auction_end_time && !isScheduled ? "top-8" : "top-2"}`}
            data-testid={`share-btn-${product.product_id}`}
            aria-label="Compartir por WhatsApp"
          >
            <Share2 size={11} />
          </button>
          
          {/* Timer for auctions (when not scheduled) */}
          {isAuction && product.auction_end_time && !isScheduled && (
            <div className="absolute top-2 right-2">
              <span className="text-[10px] bg-black/50 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                <Clock size={10} />
                <AuctionTimer endTime={product.auction_end_time} />
              </span>
            </div>
          )}
          
          {/* Countdown for scheduled products */}
          {isScheduled && product.scheduled_publish_time && (
            <div className="absolute top-2 right-2">
              <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                <Clock size={10} />
                <AuctionTimer endTime={product.scheduled_publish_time} isCountdown={true} />
              </span>
            </div>
          )}
          
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className="font-bold text-white text-sm leading-tight line-clamp-1 drop-shadow-lg">
              {product.title}
            </h3>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                {hasPromo && (
                  <span className="text-[10px] text-white/60 line-through">${product.price?.toLocaleString()}</span>
                )}
                <p className="text-lg font-bold text-white drop-shadow-lg">${currentPrice?.toLocaleString()}</p>
                {hasPromo && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid={`discount-badge-${product.product_id}`}>
                    -{product.promo_discount_percent}%
                  </span>
                )}
              </div>
              <span className="text-[10px] text-white/80 flex items-center gap-1">
                {isAuction ? (
                  <><Gavel size={10} /> {bidCount} ofertas</>
                ) : (
                  <><Users size={10} /> {requestCount} solic.</>
                )}
              </span>
            </div>
          </div>
          
          {/* Scheduled overlay */}
          {isScheduled && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-amber-500/90 px-3 py-1 rounded-full">
                <span className="text-white text-xs font-semibold">Próximamente</span>
              </div>
            </div>
          )}
        </div>
      </Link>
    );
  };

  return (
    <div className="hidden md:block px-4 pb-4">
      {/* 2x2 Grid Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* TOP LEFT: Subastas en Vivo - LARGER */}
        <div className="relative bg-white rounded-2xl p-4 shadow-sm border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Gavel size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#0F172A] text-base">Subastas en Vivo</h3>
              <p className="text-xs text-[#64748B]" data-testid="auction-quadrant-subtitle">
                {auctionsFallbackActive ? "Sin subastas por ahora · mira Mejor Oferta" : "Participa y gana al mejor precio"}
              </p>
            </div>
          </div>
          
          {auctionCanScroll.left && (
            <button
              onClick={() => scroll(auctionScrollRef, 'left')}
              className="absolute left-2 top-1/2 mt-4 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
            >
              <ChevronLeft size={18} className="text-[#374151]" />
            </button>
          )}
          {auctionCanScroll.right && (
            <button
              onClick={() => scroll(auctionScrollRef, 'right')}
              className="absolute right-2 top-1/2 mt-4 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
            >
              <ChevronRight size={18} className="text-[#374151]" />
            </button>
          )}
          
          <div 
            ref={auctionScrollRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {auctionGrids.length > 0 ? auctionGrids.map((gridProducts, idx) => (
              <div 
                key={idx} 
                className="desktop-grid-item flex-shrink-0 w-full"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="grid grid-cols-2 gap-3">
                  {gridProducts.map((product) => (
                    <LargeProductCard 
                      key={product.product_id}
                      product={product} 
                      isAuction={true}
                    />
                  ))}
                </div>
              </div>
            )) : (
              <p className="text-sm text-[#94A3B8] py-12 text-center w-full">No hay subastas activas</p>
            )}
          </div>
        </div>

        {/* TOP RIGHT: Mejor Oferta Aceptada - LARGER */}
        <div className="relative bg-white rounded-2xl p-4 shadow-sm border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <ShoppingBag size={20} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#0F172A] text-base">Mejor Oferta Aceptada</h3>
              <p className="text-xs text-[#64748B]">Envía tu mejor oferta</p>
            </div>
          </div>
          
          {directCanScroll.left && (
            <button
              onClick={() => scroll(directScrollRef, 'left')}
              className="absolute left-2 top-1/2 mt-4 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
            >
              <ChevronLeft size={18} className="text-[#374151]" />
            </button>
          )}
          {directCanScroll.right && (
            <button
              onClick={() => scroll(directScrollRef, 'right')}
              className="absolute right-2 top-1/2 mt-4 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
            >
              <ChevronRight size={18} className="text-[#374151]" />
            </button>
          )}
          
          <div 
            ref={directScrollRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {directGrids.length > 0 ? directGrids.map((gridProducts, idx) => (
              <div 
                key={idx} 
                className="desktop-grid-item flex-shrink-0 w-full"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="grid grid-cols-2 gap-3">
                  {gridProducts.map((product) => (
                    <LargeProductCard 
                      key={product.product_id}
                      product={product} 
                      isAuction={false}
                    />
                  ))}
                </div>
              </div>
            )) : (
              <p className="text-sm text-[#94A3B8] py-12 text-center w-full">No hay productos disponibles</p>
            )}
          </div>
        </div>

        {/* BOTTOM LEFT: Finalizados - GROWS WITH CONTENT */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[#F1F5F9] rounded-xl flex items-center justify-center">
              <CheckCircle size={20} className="text-[#64748B]" />
            </div>
            <div>
              <h3 className="font-bold text-[#0F172A] text-base">Finalizados</h3>
              <p className="text-xs text-[#64748B]">Historial de ventas exitosas</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {finishedProducts && finishedProducts.length > 0 ? finishedProducts.slice(0, 9).map((product) => (
              <Link 
                key={product.product_id} 
                to={`/product/${product.product_id}`}
                className="relative aspect-square rounded-xl overflow-hidden group hover:shadow-md transition-shadow"
              >
                <img 
                  src={product.image_url} 
                  alt={product.title}
                  onError={handleImgError}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end p-2">
                  <div className="text-white w-full">
                    <p className="text-xs font-medium line-clamp-1">{product.title}</p>
                    <p className="text-sm font-bold">${(product.current_bid || product.price)?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="absolute top-2 right-2">
                  <span className="text-[9px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                    VENDIDO
                  </span>
                </div>
              </Link>
            )) : (
              <p className="text-sm text-[#94A3B8] py-8 col-span-3 text-center">No hay ventas finalizadas</p>
            )}
          </div>
        </div>

        {/* BOTTOM RIGHT: Reviews - GROWS WITH CONTENT */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Star size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#0F172A] text-base">Reseñas</h3>
              <p className="text-xs text-[#64748B]">Opiniones de compradores verificados</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {reviews && reviews.length > 0 ? reviews.slice(0, 6).map((review) => (
              <div key={review.review_id} className="bg-[#F8FAFC] rounded-xl p-4 relative group hover:bg-[#F1F5F9] transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#475569] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {(review.reviewer_name || review.user_name || "U")?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="font-semibold text-[#0F172A] text-sm truncate">{review.reviewer_name || review.user_name || "Usuario"}</p>
                        {review.reviewer_auth_method === "whatsapp" && <WhatsAppBadge size="xs" />}
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            size={12} 
                            className={star <= review.rating ? "fill-amber-400 text-amber-400" : "text-[#E2E8F0]"} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-[#374151] leading-relaxed">{review.comment}</p>
                    {review.product_title && (
                      <p className="text-xs text-[#94A3B8] mt-2">
                        Producto: <span className="text-[#64748B] font-medium">{review.product_title}</span>
                      </p>
                    )}
                  </div>
                  {isAdmin && onDeleteReview && (
                    <button
                      onClick={() => onDeleteReview(review.review_id)}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-all"
                      title="Eliminar reseña"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </button>
                  )}
                </div>
              </div>
            )) : (
              <p className="text-sm text-[#94A3B8] py-8 text-center">No hay reseñas todavía</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Keep the old DualProductGridDesktop for backwards compatibility but hidden
const DualProductGridDesktop = ({ auctionProducts, directProducts }) => {
  return null; // Replaced by DesktopHomeLayout
};

export { ProductOverlayCard, ProductOverlayGrid, DualProductGridDesktop, DesktopHomeLayout };
export default ProductOverlayGrid;
